import React from "react";
import { Box, Typography } from "@mui/material";

/**
 * Travel Rule transfer status pill. Reused across withdrawal + deposit
 * transaction lists and the `/transfers/:id` detail page. Variants map 1:1
 * to the orchestrator's terminal/quasi-terminal vocab + the inbound
 * deposit equivalents:
 *
 *   pending_review   — outbound TR_AWAITING_DECISION, TR_MANUAL_REVIEW;
 *                      inbound PENDING_REVIEW
 *   action_required  — outbound TR_INFO_REQUESTED; inbound ACTION_REQUIRED
 *   rejected         — outbound TR_DECLINED / DENIED / CANCELLED / CANCELED;
 *                      inbound REJECTED
 *   authorized       — outbound TR_AUTHORIZED, FIREBLOCKS_DISPATCHED
 *                      (settled-but-not-confirmed)
 *   settled          — outbound SETTLED; inbound CONFIRMED
 *
 * Visually each variant uses the same colour vocabulary as the existing
 * MUI alert palette so the pill blends with surrounding chrome.
 */
export type TransferStatusVariant =
  | "pending_review"
  | "action_required"
  | "rejected"
  | "authorized"
  | "settled";

interface TransferStatusPillProps {
  status: TransferStatusVariant;
  size?: "sm" | "md";
  /** Optional extra label (e.g. "Outgoing" prefix). */
  label?: string;
}

const PALETTE: Record<TransferStatusVariant, { bg: string; fg: string; border: string; defaultLabel: string }> = {
  pending_review: {
    bg: "#FFF8EB",
    fg: "#B54708",
    border: "#FEDF89",
    defaultLabel: "Pending review",
  },
  action_required: {
    bg: "#FFEAEA",
    fg: "#B42318",
    border: "#FECDCA",
    defaultLabel: "Action required",
  },
  rejected: {
    bg: "#FEF3F2",
    fg: "#912018",
    border: "#FECDCA",
    defaultLabel: "Rejected",
  },
  authorized: {
    bg: "#ECFDF3",
    fg: "#067647",
    border: "#ABEFC6",
    defaultLabel: "Authorized",
  },
  settled: {
    bg: "#F2F4F7",
    fg: "#344054",
    border: "#EAECF0",
    defaultLabel: "Settled",
  },
};

const TransferStatusPill: React.FC<TransferStatusPillProps> = ({
  status,
  size = "sm",
  label,
}) => {
  const swatch = PALETTE[status];
  const display = label ?? swatch.defaultLabel;
  return (
    <Box
      data-testid={`transfer-status-pill-${status}`}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        backgroundColor: swatch.bg,
        color: swatch.fg,
        border: `1px solid ${swatch.border}`,
        borderRadius: "999px",
        px: size === "sm" ? 1.25 : 1.75,
        py: size === "sm" ? 0.25 : 0.5,
        fontSize: size === "sm" ? 11 : 12,
        fontWeight: 600,
        lineHeight: 1.4,
        textTransform: "none",
        letterSpacing: "0.02em",
      }}>
      <Typography component="span" sx={{ fontSize: "inherit", fontWeight: "inherit" }}>
        {display}
      </Typography>
    </Box>
  );
};

/**
 * Convenience: map orchestrator-vocab status string into a pill variant.
 * Returns null when no pill should be rendered (clean / settled-equivalent).
 */
export function statusToPillVariant(
  status: string | undefined | null,
): TransferStatusVariant | null {
  if (!status) return null;
  const s = status.toUpperCase();
  switch (s) {
    case "TR_AWAITING_DECISION":
    case "TR_MANUAL_REVIEW":
    case "FLAGGED":
    case "PENDING_REVIEW":
    case "PENDING":
    case "AWAITING_AUTHORIZATION":
    // UNKNOWN is the backend's forward-compat catch-all: any Notabene status
    // we haven't mapped yet (normalizeStatus coerces it to UNKNOWN). Render it
    // as a non-committal "pending review" rather than nothing, so a future
    // unmapped status can never silently show a blank UI the way FLAGGED did
    // (incident 2026-06-22). The backend warn + refetch loop still resolves it.
    case "UNKNOWN":
      return "pending_review";
    case "TR_INFO_REQUESTED":
    case "ACTION_REQUIRED":
    case "REQUIRES_PII":
      return "action_required";
    case "TR_DECLINED":
    case "REJECTED":
    case "DENIED":
    case "CANCELED":
    case "CANCELLED":
      return "rejected";
    case "TR_AUTHORIZED":
    case "FIREBLOCKS_DISPATCHED":
    case "AUTHORIZED":
      return "authorized";
    case "SETTLED":
    case "CONFIRMED":
      return "settled";
    default:
      return null;
  }
}

export default TransferStatusPill;
